import React, { useEffect, useMemo, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Button } from "./ui/button";
import { Loader2, MapPin, Trash2 } from "lucide-react";

// A styled HTML pin via divIcon — avoids the classic Leaflet+webpack broken
// marker-image problem entirely (no external icon asset to resolve).
const PIN_ICON = L.divIcon({
  className: "",
  html:
    '<div style="width:22px;height:22px;transform:translate(-50%,-100%);">' +
    '<svg viewBox="0 0 24 24" width="22" height="22" fill="#06b6d4" stroke="#0e7490" stroke-width="1.5">' +
    '<path d="M12 2c-3.9 0-7 3.1-7 7 0 5 7 13 7 13s7-8 7-13c0-3.9-3.1-7-7-7z"/>' +
    '<circle cx="12" cy="9" r="2.5" fill="#fff" stroke="none"/></svg></div>',
  iconSize: [22, 22],
  iconAnchor: [11, 22],
});

/**
 * Address field with an interactive map. The client types/confirms the address
 * (geocoded server-side on save), then a map appears with a draggable marker —
 * dragging fine-tunes the coordinates without hunting for a Google Maps link.
 *
 * Props:
 *   q            question schema
 *   currentValue saved address text (or "")
 *   meta         geocoded sidecar {lat, lng, display_name, manual?}
 *   t, saving
 *   onSave(value)          save the typed address (triggers server geocode)
 *   onSaveCoords(lat,lng)  persist a dragged marker position
 *   onClear()
 */
export default function SetupAddressField({ q, currentValue, meta, t, saving, onSave, onSaveCoords, onClear }) {
  const [draft, setDraft] = useState(currentValue || "");
  const mapRef = useRef(null);
  const mapObj = useRef(null);
  const markerObj = useRef(null);

  useEffect(() => {
    setDraft(currentValue || "");
  }, [currentValue, q.field]);

  const hasPoint = meta && typeof meta.lat === "number" && typeof meta.lng === "number";
  const dirty = (draft || "").trim() !== (currentValue || "").trim();
  const canSubmit = dirty && !saving && (draft || "").trim().length >= 5;

  // Init / tear down the Leaflet map once we have a point to show.
  useEffect(() => {
    if (!hasPoint || !mapRef.current) return;
    if (!mapObj.current) {
      const map = L.map(mapRef.current, {
        center: [meta.lat, meta.lng],
        zoom: 16,
        zoomControl: true,
        attributionControl: false,
        scrollWheelZoom: false,
      });
      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        maxZoom: 19,
      }).addTo(map);
      const marker = L.marker([meta.lat, meta.lng], { draggable: true, icon: PIN_ICON }).addTo(map);
      marker.on("dragend", () => {
        const { lat, lng } = marker.getLatLng();
        onSaveCoords(lat, lng);
      });
      mapObj.current = map;
      markerObj.current = marker;
      // The modal often mounts the map in a hidden/zero-size box; force a
      // re-measure on the next frame so tiles fill the container.
      setTimeout(() => map.invalidateSize(), 60);
    }
    return () => {
      if (mapObj.current) {
        mapObj.current.remove();
        mapObj.current = null;
        markerObj.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasPoint]);

  // Keep the marker/view in sync when the geocoded point changes (new address).
  useEffect(() => {
    if (mapObj.current && markerObj.current && hasPoint) {
      markerObj.current.setLatLng([meta.lat, meta.lng]);
      mapObj.current.setView([meta.lat, meta.lng]);
      setTimeout(() => mapObj.current && mapObj.current.invalidateSize(), 60);
    }
  }, [meta?.lat, meta?.lng, hasPoint]);

  return (
    <div className="space-y-2">
      <div className="flex gap-1.5">
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder={q.placeholder || t("answerInline")}
          disabled={saving}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              if (canSubmit) onSave(draft.trim());
            }
          }}
          className="flex-1 min-w-0 px-2 py-1.5 rounded bg-zinc-950 border border-zinc-700 focus:border-cyan-500 outline-none text-[12px] text-zinc-50"
        />
        <Button
          size="sm"
          disabled={!canSubmit}
          onClick={() => onSave(draft.trim())}
          className="h-[34px] px-3 bg-cyan-600 hover:bg-cyan-500 text-white text-xs flex-shrink-0"
        >
          {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : t("addrFind")}
        </Button>
      </div>

      {hasPoint ? (
        <div className="space-y-1">
          <div
            ref={mapRef}
            className="w-full h-[190px] rounded-lg overflow-hidden border border-zinc-700 z-0"
          />
          <div className="text-[10px] text-zinc-500 flex items-center gap-1">
            <MapPin className="h-3 w-3 text-cyan-400" />
            {t("addrDragHint")}
          </div>
        </div>
      ) : (
        currentValue && (
          <div className="text-[11px] text-zinc-500">{t("addrNoPin")}</div>
        )
      )}

      {/* Same visible delete treatment as every other field type — the muted
          grey link read as decoration. backlog c36b2393 */}
      {onClear && currentValue && (
        <button
          type="button"
          disabled={saving}
          onClick={onClear}
          title={t("clearAnswerHint")}
          className="self-start inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] text-rose-400/90 border border-rose-500/30 bg-rose-500/5 hover:bg-rose-500/15 hover:text-rose-300 disabled:opacity-50 transition-colors"
        >
          <Trash2 className="h-3 w-3" />
          {t("clearAnswerBtn")}
        </button>
      )}
    </div>
  );
}
